/**
 * RPR-6094 — Polls the Mux processing status for a video document and
 * notifies the caller whenever the backend's view of the asset changes.
 *
 *   const poller = videoStatusPollerFactory.create({
 *     documentId,
 *     onUpdate: ({status, playback_id, error_message}) => ...
 *   });
 *   poller.start();
 *   poller.stop();
 *
 * Auto-stops on terminal statuses (`ready` / `errored`). Transient HTTP /
 * network errors are swallowed and retried; after 5 consecutive failures
 * the poller gives up and stops itself.
 *
 * RPR-6923: that give-up reports `{status: 'timed_out', poll_failed: true}`, NOT
 * `errored`. `errored` is the backend's word for "Mux could not transcode this
 * video" -- a terminal, truthful failure -- and reusing it for "we could not
 * reach our own status endpoint" made every consumer state something false about
 * a video that was transcoding perfectly: rpr_video_player rendered "This video
 * could not be processed." and the Document Manager wrote a real ERRORED row.
 * Because the give-up also calls stop(), that verdict was PERMANENT for the page
 * session -- it never recovered when the API came back. At the 5s cadence roughly
 * 25s of API trouble (a deploy, a 502, a slow Redis outage) was enough. The
 * `timed_out` vocabulary already means exactly the right thing everywhere it is
 * handled -- "still processing on the server, refresh to check" -- so the fix is
 * to speak it. `poll_failed` rides alongside for the surfaces whose copy is
 * specific to a polling failure rather than to a slow transcode:
 * associated_documents' "Status unavailable" note, rpr_video_player's "Video
 * status unavailable" panel, the replace path's "Could not check the replacement
 * status" row text and toast, and the Document Manager badge's tooltip.
 * portal_helper's phIsVideoIngestFailure reads it too, as defence in depth rather
 * than copy -- nothing pairs `poll_failed` with 'errored' today.
 *
 * RPR-6741: the cadence backs off as the asset stays non-terminal -- 5s for
 * the first minute (status usually flips fast), then 10s, then 20s past five
 * minutes -- so a long large-video transcode running up to the poll cap
 * doesn't hit the status endpoint every 5s for the whole window. Each tick is
 * scheduled (via $timeout) only after the previous request settles, so there
 * are no overlapping in-flight requests.
 *
 * RPR-6752: the stop condition is progress-aware. RPR-6741's flat 15-min cap
 * fixed the original "video not available" bug (polling stopped before a large
 * asset finished transcoding), but it still cut off genuinely slow-yet-healthy
 * transcodes -- non-standard inputs (odd GOP size, HEVC, embedded edit lists)
 * can run well past 15 min on Mux. So we now keep polling up to a much higher
 * HARD ceiling (VIDEO_STATUS_POLL_MAX_HARD_MS) as long as the backend reports
 * forward progress -- either an advancing `progress.percent`, or (for a
 * non-standard input still in a processing state, which may not report a live
 * percent the whole time) the fact that it is still transcoding -- and only emit
 * `timed_out` early when progress STALLS for VIDEO_STATUS_PROGRESS_STALL_MS.
 * RPR-6747: a truly wedged asset must still time out rather than poll to the hard
 * ceiling. By design the ~8-min stall timeout applies to STANDARD inputs (an
 * advancing percent is expected, so a frozen one means "wedged"); a non-standard
 * input still in a processing state is deliberately treated as live and bounded
 * only by the hard ceiling, because Mux may legitimately report no -- or a frozen
 * -- percent for such transcodes for long stretches. The `progress` /
 * `non_standard_input` keys are backward-compatible -- older backends omit them,
 * in which case support is never detected and the poller falls back to stopping
 * at the hard ceiling.
 */
app.factory('videoStatusPollerFactory', [
  '$timeout', 'HttpAuth', 'apiHelper', 'CONSTANTS',
  function ($timeout, HttpAuth, apiHelper, CONSTANTS) {
    const API_URL = apiHelper.getApiUrl();
    const POLL_MS = CONSTANTS.VIDEO_STATUS_POLL_MS;
    const TERMINAL_STATUSES = new Set(['ready', 'errored']);
    const MAX_CONSECUTIVE_FAILURES = 5;
    // Backoff thresholds: poll at POLL_MS, then 2x after 1 min, 4x after 5 min.
    const BACKOFF_2X_AFTER_MS = 60 * 1000;
    const BACKOFF_4X_AFTER_MS = 5 * 60 * 1000;
    // RPR-6752: progress-aware poll cap. HARD_MS is the absolute ceiling;
    // PROGRESS_STALL_MS is how long we tolerate no percent advance before
    // treating the asset as stalled and timing out.
    const HARD_MAX_MS = CONSTANTS.VIDEO_STATUS_POLL_MAX_HARD_MS;
    const PROGRESS_STALL_MS = CONSTANTS.VIDEO_STATUS_PROGRESS_STALL_MS;

    class VideoStatusPoller {
      constructor(opts) {
        const options = opts || {};
        this.documentId = options.documentId;
        this.onUpdate = options.onUpdate || function () {};
        // RPR-6711: in replace mode the live `status` is already `ready` (the old
        // video keeps playing), so we track the staged replacement via
        // `replace_status` instead -- otherwise the poller would stop on the very
        // first tick and never see the swap.
        this.isReplace = !!options.isReplace;
        this._handle = null;
        this._running = false;
        this._failures = 0;
        // RPR-6752: hard ceiling (renamed from the flat VIDEO_STATUS_POLL_MAX_MS).
        this.maxDurationMs = options.maxDurationMs || HARD_MAX_MS;
        this._startedAt = 0;
        // RPR-6752: track backend progress so a stalled asset times out early
        // while a slow-but-advancing one keeps polling to the hard ceiling.
        // _lastProgressAt is seeded in start() so the initial stall window runs
        // from when polling began, not from the epoch.
        this._lastPercent = -1;
        this._lastProgressAt = 0;
        // RPR-6752: has the backend ever sent the `progress` key? Only once it has
        // do we apply the progress-stall timeout. A backend that never reports
        // `progress` (e.g. deploy skew: newer portal JS against an older API that
        // predates this feature) has no liveness signal to stall on, so it falls
        // back to the hard ceiling only -- as the class docstring promises.
        this._progressSupported = false;
      }

      _isTerminal(data) {
        if (this.isReplace) {
          // The staged replacement failed, or it resolved: replace_status
          // cleared back to "" and the live asset reached a terminal state
          // (ready after the swap, or errored). Treat a live `errored` as
          // terminal too, so a failed live asset can't poll to the cap while
          // re-emitting the failure toast every tick.
          if (data.replace_status === 'errored') { return true; }
          return !data.replace_status && TERMINAL_STATUSES.has(data.status);
        }
        return TERMINAL_STATUSES.has(data.status);
      }

      start() {
        if (this._running) return; // already running
        this._running = true;
        this._failures = 0;
        this._startedAt = Date.now();
        // RPR-6752: seed the stall clock at start so "no progress yet" is measured
        // from the first tick, not the epoch.
        this._lastProgressAt = this._startedAt;
        this._lastPercent = -1;
        this._progressSupported = false;
        // Fire once immediately so the UI doesn't wait POLL_MS for first state.
        this._poll();
      }

      stop() {
        this._running = false;
        if (this._handle) {
          $timeout.cancel(this._handle);
          this._handle = null;
        }
      }

      _scheduleNext() {
        const elapsed = Date.now() - this._startedAt;
        let delay = POLL_MS;
        if (elapsed >= BACKOFF_4X_AFTER_MS) {
          delay = POLL_MS * 4;
        } else if (elapsed >= BACKOFF_2X_AFTER_MS) {
          delay = POLL_MS * 2;
        }
        this._handle = $timeout(() => this._poll(), delay);
      }

      _poll() {
        this._handle = null; // the scheduled tick (if any) has now fired
        if (!this._running) return; // stopped between scheduling and firing
        // RPR-6752: stop at the absolute hard ceiling OR when progress has stalled
        // (no `progress.percent` advance) for PROGRESS_STALL_MS. RPR-6741 needs a
        // slow-but-healthy transcode to keep polling; RPR-6747 needs a genuinely
        // wedged asset to still give up instead of polling to the ceiling.
        if (this._startedAt) {
          const now = Date.now();
          const hitHardCeiling = (now - this._startedAt) >= this.maxDurationMs;
          // Only stall-time-out once the backend has shown it reports progress;
          // otherwise (no `progress` key ever) fall back to the hard ceiling only.
          const progressStalled = this._progressSupported
            && (now - this._lastProgressAt) >= PROGRESS_STALL_MS;
          if (hitHardCeiling || progressStalled) {
            this.onUpdate({ status: 'timed_out' });
            this.stop();
            return;
          }
        }
        const url = `${API_URL}/api/authoring/video/${this.documentId}/status/`;
        HttpAuth.get(url).then((response) => {
          this._failures = 0;
          const data = response?.data || {};
          // RPR-6752: once the backend includes the `progress` key, the stall
          // timeout is in play. Detecting support for the first time (e.g. a
          // deploy skew resolving mid-poll) reseeds the stall clock so a long-stale
          // _lastProgressAt can't fire a spurious immediate timeout.
          // `progress` absent over the wire reads as undefined (JSON has no
          // undefined, so a present key is null or an object) -- this cleanly
          // distinguishes an older backend that omits the key from one reporting
          // `progress: null` (still ingesting / cache miss).
          if (data.progress !== undefined) {
            if (!this._progressSupported) { this._lastProgressAt = Date.now(); }
            this._progressSupported = true;
          }
          // RPR-6752: a real percent advance resets the stall clock so a slow but
          // progressing asset polls up to the hard ceiling. Guard on `progress`
          // existing + numeric percent so older backends (no `progress` key) fall
          // back cleanly to the hard-ceiling-only behaviour.
          if (data.progress && typeof data.progress.percent === 'number'
            && data.progress.percent > this._lastPercent) {
            this._lastPercent = data.progress.percent;
            this._lastProgressAt = Date.now();
          } else if (data.non_standard_input && !this._isTerminal(data)) {
            // Non-standard inputs (odd GOP / HEVC / embedded edit lists) transcode
            // for tens of minutes and may not report a live percent the whole time.
            // Treat "non-standard + still processing" as liveness so a healthy slow
            // transcode keeps polling to the hard ceiling instead of prematurely
            // stall-timing-out (RPR-6741); the hard ceiling still bounds it (RPR-6747).
            this._lastProgressAt = Date.now();
          }
          this.onUpdate(data);
          if (this._isTerminal(data)) {
            this.stop();
          }
        }, (err) => {
          this._failures += 1;
          // Log but don't crash on transient errors (5xx, network blips).
          // eslint-disable-next-line no-console
          console.warn('VideoStatusPoller: status fetch failed', this.documentId, err?.status, err);
          if (this._failures >= MAX_CONSECUTIVE_FAILURES) {
            // RPR-6719 added `poll_failed` here so the upload dialog would stop
            // telling an EM their file was corrupted when the only thing that had
            // failed was our polling; it left `status: 'errored'` alone to avoid
            // disturbing the other consumers. RPR-6923 finishes that job at the
            // source: 'timed_out', never 'errored' -- see the note in the file
            // header. The asset is almost certainly still transcoding; what failed
            // is our ability to observe it, and stop() makes this verdict final.
            // `error_message: 'polling failed'` goes with it: its only reader was
            // the Document Manager's `ng-if="upload.status === 'errored'"` badge
            // tooltip, which no longer fires for a lost-contact update.
            this.onUpdate({ status: 'timed_out', poll_failed: true });
            this.stop();
          }
        }).finally(() => {
          // Reschedule only if still running (not stopped by terminal/timeout/failure).
          if (this._running) this._scheduleNext();
        });
      }
    }

    return {
      create: function (opts) { return new VideoStatusPoller(opts); },
      VideoStatusPoller: VideoStatusPoller
    };
  }
]);
